import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_CONFIG } from '../config';
import { ProjectRole } from '../types/project';

interface User {
  id: number;
  email: string;
  name: string;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
}

interface RegisterData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  privacy_policy_accepted: boolean;
  terms_of_service_accepted: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user data
      fetch(`${process.env.REACT_APP_API_URL?.replace('http://', 'https://')}${API_CONFIG.ENDPOINTS.AUTH.ME}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
        .then(response => {
          if (!response.ok) {
            if (response.status === 401) {
              localStorage.removeItem('token');
              setUser(null);
              throw new Error('Session expired. Please log in again.');
            }
            throw new Error('Failed to fetch user data');
          }
          return response.json();
        })
        .then((data: User) => {
          console.log('User data from server:', data);
          setUser(data);
        })
        .catch((error) => {
          console.error('Error fetching user data:', error);
          setError(error.message);
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      const loginData = {
        email,
        password
      };

      console.log('Attempting login with:', { email });
      console.log('API URL:', `${process.env.REACT_APP_API_URL?.replace('http://', 'https://')}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`);

      const response = await fetch(`${process.env.REACT_APP_API_URL?.replace('http://', 'https://')}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(loginData),
      });
      
      console.log('Login response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Login error response:', errorData);
        throw new Error(errorData.detail || 'Invalid email or password');
      }

      const data: LoginResponse = await response.json();
      console.log('Login successful, token received');
      localStorage.setItem('token', data.access_token);
      
      // Fetch user data after successful login
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL?.replace('http://', 'https://')}${API_CONFIG.ENDPOINTS.AUTH.ME}`, {
        headers: { 
          'Authorization': `Bearer ${data.access_token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }
      
      const userData: User = await userResponse.json();
      console.log('User data after login:', userData);
      setUser(userData);
    } catch (error) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during login');
      localStorage.removeItem('token');
      setUser(null);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL?.replace('http://', 'https://')}${API_CONFIG.ENDPOINTS.AUTH.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }
      
      // Registration successful - do NOT auto-login since users are inactive by default
      // They need to get access through Telegram bot first
      console.log('Registration successful');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred during registration');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 